import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import RoomIcon from "@mui/icons-material/Room";
import apiService from "../../../service/Api";
import { loadPlacesLibrary } from "../../../lib/google-maps";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../components/common/ShowToast";

interface SelectedPlace {
  description: string;
  lat: number;
  lon: number;
  photoUrls: string[];
}

interface VenueGeocodeButtonProps {
  venueId?: number;
  latitude?: number | null;
  longitude?: number | null;
  addressOne: string;
  addressTwo?: string;
  suburb?: string;
  city?: string;
  venueName?: string;
  onConfirm: (lat: number, lon: number) => void;
}

export default function VenueGeocodeButton({
  venueId,
  latitude,
  longitude,
  addressOne,
  addressTwo,
  suburb,
  city,
  venueName,
  onConfirm,
}: VenueGeocodeButtonProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [predictions, setPredictions] = useState<
    google.maps.places.PlacePrediction[]
  >([]);
  const [selected, setSelected] = useState<SelectedPlace | null>(null);

  const placesRef = useRef<google.maps.PlacesLibrary | null>(null);
  const sessionTokenRef =
    useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const runSearch = async (text: string) => {
    if (text.trim().length < 3) {
      setPredictions([]);
      return;
    }
    if (!placesRef.current) return;

    setSearching(true);
    try {
      const { AutocompleteSuggestion } = placesRef.current;
      const { suggestions } =
        await AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: text,
          sessionToken: sessionTokenRef.current ?? undefined,
          includedRegionCodes: ["zw"],
        });
      setPredictions(
        suggestions
          .map((s) => s.placePrediction)
          .filter((p): p is google.maps.places.PlacePrediction => p !== null),
      );
    } catch {
      showErrorToast("Failed to search for the venue. Please try again.");
      setPredictions([]);
    } finally {
      setSearching(false);
    }
  };

  const handleOpen = async () => {
    setOpen(true);
    setSelected(null);
    setLoadError(null);
    const initialQuery = [venueName, addressOne, addressTwo, suburb, city]
      .filter((part) => part && part.trim())
      .join(", ");
    setQuery(initialQuery);

    if (!placesRef.current) {
      try {
        placesRef.current = await loadPlacesLibrary();
      } catch (err) {
        console.error("Failed to load Google Places library:", err);
        const message = err instanceof Error ? err.message : String(err);
        setLoadError(`Could not load Google Maps: ${message}`);
        return;
      }
    }

    sessionTokenRef.current =
      new placesRef.current.AutocompleteSessionToken();
    runSearch(initialQuery);
  };

  const handleQueryChange = (value: string) => {
    setQuery(value);
    setSelected(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(value), 300);
  };

  const handleClose = () => {
    if (confirming) return;
    setOpen(false);
  };

  const handleSelectPrediction = async (
    prediction: google.maps.places.PlacePrediction,
  ) => {
    setResolving(true);
    try {
      const place = prediction.toPlace();
      await place.fetchFields({
        fields: ["displayName", "formattedAddress", "location", "photos"],
      });
      if (!place.location) {
        showErrorToast("That place has no coordinates. Try another match.");
        return;
      }
      const photoUrls = (place.photos ?? [])
        .slice(0, 2)
        .map((photo) => photo.getURI({ maxWidth: 400, maxHeight: 300 }));
      setSelected({
        description:
          place.formattedAddress || place.displayName || prediction.text.text,
        lat: place.location.lat(),
        lon: place.location.lng(),
        photoUrls,
      });
    } catch {
      showErrorToast("Failed to resolve that place. Please try again.");
    } finally {
      setResolving(false);
    }
  };

  const handleConfirm = async () => {
    if (!selected) return;

    if (!venueId) {
      onConfirm(selected.lat, selected.lon);
      sessionTokenRef.current = null;
      setOpen(false);
      return;
    }

    setConfirming(true);
    try {
      await apiService.patch(
        `/api/venues/venue/${venueId}/geocode-confirm/`,
        { latitude: selected.lat, longitude: selected.lon },
      );
      onConfirm(selected.lat, selected.lon);
      showSuccessToast("Venue coordinates updated.");
      sessionTokenRef.current = null;
      setOpen(false);
    } catch {
      showErrorToast("Failed to save coordinates. Please try again.");
    } finally {
      setConfirming(false);
    }
  };

  const hasCoordinates = latitude != null && longitude != null;

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<RoomIcon />}
          onClick={handleOpen}
        >
          {hasCoordinates ? "Update Coordinates" : "Find Coordinates"}
        </Button>
        <Typography variant="caption" color="text.secondary">
          {hasCoordinates
            ? `${latitude!.toFixed(8)}, ${longitude!.toFixed(8)}`
            : "No coordinates set"}
        </Typography>
      </Box>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>
          {venueName ? `Find coordinates for "${venueName}"` : "Find coordinates"}
        </DialogTitle>
        <DialogContent dividers>
          {loadError ? (
            <Typography variant="body2" color="error">
              {loadError}
            </Typography>
          ) : (
            <>
              <TextField
                fullWidth
                autoFocus
                placeholder="Start typing the venue's address..."
                value={query}
                onChange={(e) => handleQueryChange(e.target.value)}
                size="small"
                sx={{ mb: 2 }}
              />

              {searching ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    py: 4,
                  }}
                >
                  <CircularProgress size={28} />
                </Box>
              ) : predictions.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {query.trim().length < 3
                    ? "Keep typing at least 3 characters to search."
                    : "No matches yet. Try the street address, suburb, or a nearby landmark."}
                </Typography>
              ) : (
                <List>
                  {predictions.map((prediction) => (
                    <ListItemButton
                      key={prediction.placeId}
                      selected={selected?.description === prediction.text.text}
                      disabled={resolving}
                      onClick={() => handleSelectPrediction(prediction)}
                      sx={{ borderRadius: 1, mb: 0.5 }}
                    >
                      <ListItemText primary={prediction.text.text} />
                    </ListItemButton>
                  ))}
                </List>
              )}

              {selected && (
                <Box
                  sx={{
                    mt: 2,
                    p: 1.5,
                    borderRadius: 1,
                    bgcolor: "action.hover",
                  }}
                >
                  <Typography variant="body2" fontWeight={600}>
                    Selected: {selected.description}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selected.lat.toFixed(8)}, {selected.lon.toFixed(8)}
                  </Typography>
                  {selected.photoUrls.length > 0 && (
                    <Box sx={{ display: "flex", gap: 1, mt: 1.5 }}>
                      {selected.photoUrls.map((url) => (
                        <Box
                          key={url}
                          component="img"
                          src={url}
                          alt="Venue"
                          sx={{
                            width: 160,
                            height: 120,
                            objectFit: "cover",
                            borderRadius: 1,
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={confirming}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={!selected || confirming || resolving}
          >
            {confirming ? "Saving..." : "Confirm This Is the Venue"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
